import { useState, useMemo, useCallback } from 'react';
import {
    Button,
    Input,
    Label,
    Skeleton,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@ui';
import {
    Search,
    Filter,
    Download,
    Receipt,
    Package,
    CreditCard,
    Coins,
    Building2,
    ChevronDown,
    ChevronUp,
    MoreVertical,
    Eye,
    Edit2,
    Trash2,
    Calendar,
    RefreshCw,
    X,
    Loader2,
} from 'lucide-react';
import { cn } from '@ui/lib/utils';
import { toastMessage } from '@ui/components/Toast';
import { useGroupStore } from '@/stores/groupStore';
import {
    useGetBillingList,
    useDeleteBilling,
} from '@hooks/generated/charge';
import { useQueryClient } from '@tanstack/react-query';
import type { BillingStatus, PaymentMethod, ApiBillingItem } from './types';
import {
    getBillingStatusLabel,
    getBillingStatusStyle,
    getPaymentMethodLabel,
    formatAmount,
    formatDateTime,
    BILLING_STATUS_OPTIONS,
} from './utils';
import { BillingDetailModal } from './components';

// 기간 요약 정보 계산
function calculateSummary(billings: ApiBillingItem[]) {
    const total = billings.length;
    const settled = billings.filter(b => b.status === 'SETTLED').length;
    const partiallySettled = billings.filter(b => b.status === 'PARTIALLY_SETTLED').length;
    const partiallyRefunded = billings.filter(b => b.status === 'PARTIALLY_REFUNDED').length;
    const fullyRefunded = billings.filter(b => b.status === 'FULLY_REFUNDED').length;
    const unsettled = billings.filter(b => b.status === 'UNSETTLED').length;

    const totalAmount = billings.reduce((sum, b) => sum + (b.finalAmount || 0), 0);
    const paidAmount = billings.reduce((sum, b) => sum + (b.totalPaidAmount || 0), 0);
    const refundedAmount = billings.reduce((sum, b) => sum + (b.totalRefundedAmount || 0), 0);
    const voucherSettlementTotal = billings.reduce((sum, b) => sum + (b.voucherSettlementAmount || 0), 0);

    return {
        total,
        settled,
        partiallySettled,
        partiallyRefunded,
        fullyRefunded,
        unsettled,
        totalAmount,
        paidAmount,
        refundedAmount,
        voucherSettlementTotal,
    };
}

// 검색 조건 타입 정의 (SchedulesPage 형식과 동일)
interface SearchQuery {
    keyword: string;           // 보호자/반려동물 검색어
    status: BillingStatus | 'ALL';
    formStartDate: string;     // 시작일 (YYYY-MM-DD)
    formEndDate: string;       // 종료일 (YYYY-MM-DD)
}

// 기본 기간 계산 (현재 기준 1달 전)
function getDefaultDates() {
    const today = new Date();
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    const formatDate = (date: Date) => {
        return date.toISOString().split('T')[0];
    };

    return {
        formStartDate: formatDate(oneMonthAgo),
        formEndDate: formatDate(today),
    };
}

// 날짜 문자열을 ISO 형식으로 변환 (API 요청용)
// "YYYY-MM-DD" -> "YYYY-MM-DDTHH:mm:ss.sssZ"
function toISOStartDate(dateStr: string): string {
    // 시작일: 해당 날짜의 00:00:00.000Z
    return `${dateStr}T00:00:00.000Z`;
}

function toISOEndDate(dateStr: string): string {
    // 종료일: 해당 날짜의 23:59:59.999Z
    return `${dateStr}T23:59:59.999Z`;
}

// 기본 검색 조건
const DEFAULT_SEARCH_QUERY: SearchQuery = {
    keyword: '',
    status: 'ALL',
    ...getDefaultDates(),
};

export default function BillingListPage() {
    const selectedGroup = useGroupStore((state) => state.selectedGroup);
    const groupId = selectedGroup?.id;
    const queryClient = useQueryClient();

    // 검색 조건 상태 (API 호출에 사용)
    const [searchQuery, setSearchQuery] = useState<SearchQuery>(DEFAULT_SEARCH_QUERY);

    // 임시 검색 조건 상태 (입력 필드용)
    const [tempSearchQuery, setTempSearchQuery] = useState<SearchQuery>(DEFAULT_SEARCH_QUERY);

    // API 훅 - 정산 목록 조회 (검색 조건 파라미터 포함)
    // Note: API는 현재 groupId, status, startDate, endDate만 지원
    // keyword는 향후 백엔드 지원 필요
    const {
        data: billingListData,
        isLoading: isLoadingBillings,
        isFetching: isFetchingBillings,
        refetch: refetchBillings,
    } = useGetBillingList({
        request: {
            groupId: groupId!,
            status: searchQuery.status !== 'ALL' ? searchQuery.status : undefined,
            // 날짜를 ISO 8601 형식으로 변환 ("2026-01-01T00:00:00.000Z")
            startDate: searchQuery.formStartDate ? toISOStartDate(searchQuery.formStartDate) : undefined,
            endDate: searchQuery.formEndDate ? toISOEndDate(searchQuery.formEndDate) : undefined,
        },
        options: { enabled: !!groupId },
    });

    // 새로고침 핸들러
    const handleRefresh = useCallback(async () => {
        try {
            const result = await refetchBillings();
            if (result.isSuccess) {
                toastMessage({ message: '정산 목록을 새로고침했습니다.', type: 'success' });
            } else if (result.isError && result.error) {
                const errorMessage = (result.error as any)?.response?.data?.message || '새로고침에 실패했습니다.';
                toastMessage({ message: errorMessage, type: 'error' });
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || '새로고침에 실패했습니다.';
            toastMessage({ message: errorMessage, type: 'error' });
        }
    }, [refetchBillings]);

    // 정산 삭제 뮤테이션
    const deleteBillingMutation = useDeleteBilling({
        options: {
            onSuccess: () => {
                toastMessage({ message: '정산이 삭제되었습니다.', type: 'success' });
                queryClient.invalidateQueries({ queryKey: ['getBillingList'] });
            },
            onError: () => {
                toastMessage({ message: '정산 삭제에 실패했습니다.', type: 'error' });
            },
        },
    });

    // API 응답을 배열로 변환하고 필드명 매핑
    const billings = useMemo(() => {
        if (!billingListData) return [];

        // API 응답에서 배열 추출
        let rawData: unknown[] = [];
        if (Array.isArray(billingListData)) {
            rawData = billingListData;
        } else {
            const data = billingListData as unknown as { content?: unknown[]; items?: unknown[] };
            if (data.content) rawData = data.content;
            else if (data.items) rawData = data.items;
        }

        // BillingResponse -> ApiBillingItem 필드명 매핑
        // API uses: totalPaid, totalRefunded, voucherSettlementTotal
        // ApiBillingItem uses: totalPaidAmount, totalRefundedAmount, voucherSettlementAmount
        return rawData.map((item: unknown) => {
            const apiItem = item as Record<string, unknown>;
            return {
                ...apiItem,
                totalPaidAmount: apiItem.totalPaid ?? apiItem.totalPaidAmount,
                totalRefundedAmount: apiItem.totalRefunded ?? apiItem.totalRefundedAmount,
                voucherSettlementAmount: apiItem.voucherSettlementTotal ?? apiItem.voucherSettlementAmount,
            } as ApiBillingItem;
        });
    }, [billingListData]);

    // 필터 열림 상태
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // 활성 필터 여부 확인 (기본값과 다른 필터가 적용되어 있는지)
    const hasActiveFilter = useMemo(() => {
        return searchQuery.keyword !== DEFAULT_SEARCH_QUERY.keyword ||
            searchQuery.status !== DEFAULT_SEARCH_QUERY.status ||
            searchQuery.formStartDate !== DEFAULT_SEARCH_QUERY.formStartDate ||
            searchQuery.formEndDate !== DEFAULT_SEARCH_QUERY.formEndDate;
    }, [searchQuery]);

    // 정렬 상태
    const [sortBy, setSortBy] = useState<'createdAt' | 'finalAmount' | 'memberName'>('createdAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    // 페이지네이션
    const [page, setPage] = useState(1);
    const itemsPerPage = 10;

    // 선택된 정산 (상세보기/수정용)
    const [selectedBilling, setSelectedBilling] = useState<ApiBillingItem | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    // 액션 메뉴 상태
    const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);

    // 정렬된 정산 목록
    // API 미지원 필터(keyword)만 로컬에서 처리, 나머지는 API에서 처리
    const sortedBillings = useMemo(() => {
        let result = [...billings];

        // keyword 필터 (API 미지원 - 로컬 필터링)
        if (searchQuery.keyword) {
            const keyword = searchQuery.keyword.toLowerCase();
            result = result.filter(b =>
                (b.memberName || '').toLowerCase().includes(keyword) ||
                (b.petName || '').toLowerCase().includes(keyword)
            );
        }

        // 정렬
        result.sort((a, b) => {
            let comparison = 0;
            if (sortBy === 'createdAt') {
                comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            } else if (sortBy === 'finalAmount') {
                comparison = (a.finalAmount || 0) - (b.finalAmount || 0);
            } else if (sortBy === 'memberName') {
                comparison = (a.memberName || '').localeCompare(b.memberName || '');
            }
            return sortOrder === 'asc' ? comparison : -comparison;
        });

        return result;
    }, [billings, searchQuery.keyword, sortBy, sortOrder]);

    // 페이지네이션된 목록
    const paginatedBillings = useMemo(() => {
        const start = (page - 1) * itemsPerPage;
        return sortedBillings.slice(start, start + itemsPerPage);
    }, [sortedBillings, page]);

    const totalPages = Math.ceil(sortedBillings.length / itemsPerPage);

    // 요약 정보
    const summary = useMemo(() => calculateSummary(sortedBillings), [sortedBillings]);

    // 검색 버튼 클릭 시 필터 적용 (API 호출)
    const handleSearch = useCallback(() => {
        // searchQuery 업데이트
        setSearchQuery({ ...tempSearchQuery });
        setPage(1);
        // 쿼리 캐시 무효화하여 강제 재조회 (queryKey에 포함되지 않은 파라미터 변경 시에도 동작하도록)
        queryClient.invalidateQueries({ queryKey: ['getBillingList'] });
    }, [tempSearchQuery, queryClient]);

    // 필터 초기화
    const handleResetFilters = useCallback(() => {
        // 임시 검색 조건 초기화
        setTempSearchQuery(DEFAULT_SEARCH_QUERY);
        // 검색 조건 초기화
        setSearchQuery(DEFAULT_SEARCH_QUERY);
        setPage(1);
        // 쿼리 캐시 무효화하여 강제 재조회
        queryClient.invalidateQueries({ queryKey: ['getBillingList'] });
    }, [queryClient]);

    // 정산 상세보기
    const handleViewDetail = useCallback((billing: ApiBillingItem) => {
        setSelectedBilling(billing);
        setShowDetailModal(true);
        setOpenActionMenuId(null);
    }, []);

    // 정산 수정
    const handleEdit = useCallback((_billing: ApiBillingItem) => {
        toastMessage({ message: '정산 수정 기능은 일정 상세에서 사용해주세요.', type: 'warning' });
        setOpenActionMenuId(null);
    }, []);

    // 정산 삭제
    const handleDelete = useCallback((billing: ApiBillingItem) => {
        if (window.confirm(`${billing.memberName || '회원'}님의 정산을 삭제하시겠습니까?`)) {
            deleteBillingMutation.mutate({
                groupId: groupId!,
                billingId: billing.id,
            });
        }
        setOpenActionMenuId(null);
    }, [groupId, deleteBillingMutation]);

    // Excel 내보내기 (CSV 형식)
    const handleExportExcel = useCallback(() => {
        if (sortedBillings.length === 0) {
            toastMessage({ message: '내보낼 데이터가 없습니다.', type: 'warning' });
            return;
        }

        // CSV 헤더
        const headers = [
            '정산ID',
            '정산일시',
            '보호자',
            '반려동물',
            '이용권',
            '이용권 사용 금액',
            '정산금',
            '최종금액',
            '환불금액',
            '결제방법',
            '상태',
        ];

        // CSV 데이터 행
        const rows = sortedBillings.map((billing) => [
            `BIL-${String(billing.id).padStart(4, '0')}`,
            new Date(new Date(billing.createdAt).getTime() + 9 * 60 * 60 * 1000).toLocaleString('ko-KR', { timeZone: 'UTC' }),
            billing.memberName || '-',
            billing.petName || '-',
            billing.subscriptionName || '직접입력',
            billing.voucherSettlementAmount ? `-${billing.voucherSettlementAmount}` : '-',
            billing.totalPaidAmount || 0,
            billing.finalAmount || 0,
            billing.totalRefundedAmount || 0,
            billing.paymentMethod ? getPaymentMethodLabel(billing.paymentMethod) : '-',
            getBillingStatusLabel(billing.status),
        ]);

        // CSV 문자열 생성 (쉼표가 포함된 값은 따옴표로 감싸기)
        const escapeCSV = (value: string | number) => {
            const str = String(value);
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const csvContent = [
            headers.map(escapeCSV).join(','),
            ...rows.map(row => row.map(escapeCSV).join(',')),
        ].join('\n');

        // 파일 다운로드
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const today = new Date().toISOString().split('T')[0];
        link.download = `정산내역_${searchQuery.formStartDate}_${searchQuery.formEndDate}_${today}.csv`;
        link.click();
        URL.revokeObjectURL(url);

        toastMessage({ message: `${sortedBillings.length}건의 정산 내역을 내보냈습니다.`, type: 'success' });
    }, [sortedBillings, searchQuery.formStartDate, searchQuery.formEndDate]);

    // 정렬 토글
    const handleSortToggle = useCallback((field: typeof sortBy) => {
        if (sortBy === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    }, [sortBy]);

    if (!groupId) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">그룹을 먼저 선택해주세요.</p>
            </div>
        );
    }

    if (isLoadingBillings) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
                <span className="ml-2 text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">정산 목록을 불러오는 중...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* 헤더 */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-neutral-800 dark:text-neutral-100">정산 관리</h1>
                    <p className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">정산 내역을 확인하고 관리하세요.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={handleRefresh}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                        disabled={isFetchingBillings}
                    >
                        <RefreshCw className={cn("h-4 w-4", isFetchingBillings && "animate-spin")} />
                        새로고침
                    </Button>
                    <Button
                        onClick={handleExportExcel}
                        variant="outline"
                        className="flex items-center gap-2"
                    >
                        <Download className="h-4 w-4" />
                        Excel 내보내기
                    </Button>
                </div>
            </div>

            {/* Filter Toggle Button */}
            <div className="flex items-center gap-4">
                <Button
                    variant={isFilterOpen || hasActiveFilter ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                    <Filter className="mr-2 h-4 w-4" />
                    검색 필터
                    {hasActiveFilter && (
                        <span className="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-white dark:bg-neutral-900 text-xs font-medium text-orange-600">
                            !
                        </span>
                    )}
                    {isFilterOpen ? (
                        <ChevronUp className="ml-2 h-4 w-4" />
                    ) : (
                        <ChevronDown className="ml-2 h-4 w-4" />
                    )}
                </Button>
                {hasActiveFilter && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleResetFilters}
                        className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500"
                    >
                        <X className="h-4 w-4 mr-1" />
                        필터 초기화
                    </Button>
                )}
            </div>

            {/* 필터 및 검색 영역 */}
            {isFilterOpen && (
                <div className="bg-white dark:bg-neutral-900 rounded-lg border p-4 space-y-4">
                    {/* Row 1: 검색, 상태 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* 검색 */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">검색</Label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500" />
                                <Input
                                    value={tempSearchQuery.keyword}
                                    onChange={(e) => setTempSearchQuery(prev => ({ ...prev, keyword: e.target.value }))}
                                    placeholder="보호자/반려동물 검색"
                                    className="pl-10"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearch();
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        {/* 상태 필터 */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">상태</Label>
                            <select
                                value={tempSearchQuery.status}
                                onChange={(e) => setTempSearchQuery(prev => ({ ...prev, status: e.target.value as BillingStatus | 'ALL' }))}
                                className="w-full h-10 px-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            >
                                {BILLING_STATUS_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Row 2: 기간 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* 기간 필터 */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">기간</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="date"
                                    value={tempSearchQuery.formStartDate}
                                    onChange={(e) => setTempSearchQuery(prev => ({ ...prev, formStartDate: e.target.value }))}
                                    className="flex-1"
                                />
                                <span className="text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">~</span>
                                <Input
                                    type="date"
                                    value={tempSearchQuery.formEndDate}
                                    onChange={(e) => setTempSearchQuery(prev => ({ ...prev, formEndDate: e.target.value }))}
                                    className="flex-1"
                                />
                            </div>
                        </div>
                    </div>

                    {/* 검색 / 초기화 버튼 */}
                    <div className="flex items-center justify-end gap-2 pt-2 border-t">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleResetFilters}
                            className="flex items-center gap-2"
                        >
                            <RefreshCw className="h-4 w-4" />
                            초기화
                        </Button>
                        <Button
                            variant="default"
                            size="sm"
                            onClick={handleSearch}
                            className="flex items-center gap-2"
                            disabled={isFetchingBillings}
                        >
                            {isFetchingBillings ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Search className="h-4 w-4" />
                            )}
                            검색
                        </Button>
                    </div>
                </div>
            )}

            {/* 요약 카드 */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-white dark:bg-neutral-900 rounded-lg border p-4">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mb-1">총 정산</p>
                    <p className="text-xl font-bold text-neutral-800 dark:text-neutral-100">{summary.total}건</p>
                </div>
                <div className="bg-white dark:bg-neutral-900 rounded-lg border p-4">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mb-1">정산완료</p>
                    <p className="text-xl font-bold text-green-600">{summary.settled}건</p>
                </div>
                <div className="bg-white dark:bg-neutral-900 rounded-lg border p-4">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mb-1">부분정산</p>
                    <p className="text-xl font-bold text-yellow-600">{summary.partiallySettled}건</p>
                </div>
                <div className="bg-white dark:bg-neutral-900 rounded-lg border p-4">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mb-1">환불</p>
                    <p className="text-xl font-bold text-orange-600">{summary.partiallyRefunded + summary.fullyRefunded}건</p>
                </div>
                <div className="bg-white dark:bg-neutral-900 rounded-lg border p-4">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mb-1">미정산</p>
                    <p className="text-xl font-bold text-neutral-600 dark:text-neutral-300">{summary.unsettled}건</p>
                </div>
            </div>

            {/* 금액 요약 */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200 p-4">
                <div className="flex flex-wrap gap-6">
                    <div>
                        <p className="text-xs text-orange-600 mb-1">총 정산 금액</p>
                        <p className="text-lg font-bold text-orange-700">{formatAmount(summary.totalAmount)}</p>
                    </div>
                    <div className="border-l border-orange-200 pl-6">
                        <p className="text-xs text-neutral-600 dark:text-neutral-300 mb-1">결제 금액</p>
                        <p className="text-lg font-bold text-neutral-800 dark:text-neutral-100">{formatAmount(summary.paidAmount)}</p>
                    </div>
                    <div className="border-l border-orange-200 pl-6">
                        <p className="text-xs text-red-600 mb-1">환불 금액</p>
                        <p className="text-lg font-bold text-red-600">-{formatAmount(summary.refundedAmount)}</p>
                    </div>
                    {summary.voucherSettlementTotal > 0 && (
                        <div className="border-l border-orange-200 pl-6">
                            <p className="text-xs text-blue-600 mb-1">이용권 정산금 합계</p>
                            <p className="text-lg font-bold text-blue-600">{formatAmount(summary.voucherSettlementTotal)}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* 정산 목록 테이블 */}
            <div className="bg-white dark:bg-neutral-900 rounded-lg border overflow-visible">
                <div className="overflow-x-auto overflow-y-visible relative">
                    <table className="w-full">
                        <thead className="bg-neutral-50 dark:bg-neutral-800/50 border-b">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300">정산ID</th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 cursor-pointer hover:bg-neutral-100 dark:bg-neutral-800 dark:hover:bg-neutral-800"
                                    onClick={() => handleSortToggle('createdAt')}
                                >
                                    <div className="flex items-center gap-1">
                                        정산일시
                                        {sortBy === 'createdAt' && (
                                            sortOrder === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />
                                        )}
                                    </div>
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 cursor-pointer hover:bg-neutral-100 dark:bg-neutral-800 dark:hover:bg-neutral-800"
                                    onClick={() => handleSortToggle('memberName')}
                                >
                                    <div className="flex items-center gap-1">
                                        보호자/반려동물
                                        {sortBy === 'memberName' && (
                                            sortOrder === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />
                                        )}
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300">이용권</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-600 dark:text-neutral-300">이용권 사용 금액</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-600 dark:text-neutral-300">정산금</th>
                                <th
                                    className="px-4 py-3 text-right text-xs font-semibold text-neutral-600 dark:text-neutral-300 cursor-pointer hover:bg-neutral-100 dark:bg-neutral-800 dark:hover:bg-neutral-800"
                                    onClick={() => handleSortToggle('finalAmount')}
                                >
                                    <div className="flex items-center justify-end gap-1">
                                        최종금액
                                        {sortBy === 'finalAmount' && (
                                            sortOrder === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />
                                        )}
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-600 dark:text-neutral-300">결제방법</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-600 dark:text-neutral-300">상태</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-600 dark:text-neutral-300">액션</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {paginatedBillings.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="px-4 py-12 text-center text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                        <Receipt className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
                                        <p>정산 내역이 없습니다.</p>
                                    </td>
                                </tr>
                            ) : (
                                paginatedBillings.map((billing) => {
                                    const statusStyle = getBillingStatusStyle(billing.status);

                                    return (
                                        <tr key={billing.id} className="hover:bg-neutral-50 dark:bg-neutral-800 dark:hover:bg-neutral-800">
                                            <td className="px-4 py-3">
                                                <button
                                                    onClick={() => handleViewDetail(billing)}
                                                    className="text-sm font-mono text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                                >
                                                    BIL-{String(billing.id).padStart(4, '0')}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3">
                                                <p className="text-sm text-neutral-800 dark:text-neutral-100">
                                                    {new Date(new Date(billing.createdAt).getTime() + 9 * 60 * 60 * 1000).toLocaleDateString('ko-KR', {
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        timeZone: 'UTC',
                                                    })}
                                                </p>
                                                <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                                    {new Date(new Date(billing.createdAt).getTime() + 9 * 60 * 60 * 1000).toLocaleTimeString('ko-KR', {
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        timeZone: 'UTC',
                                                    })}
                                                </p>
                                            </td>
                                            <td className="px-4 py-3">
                                                <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100">{billing.memberName}</p>
                                                <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">{billing.petName}</p>
                                            </td>
                                            <td className="px-4 py-3">
                                                {billing.billingType === 'SUBSCRIPTION' && billing.subscriptionName ? (
                                                    <div className="flex items-center gap-1">
                                                        <Package className="h-3 w-3 text-blue-500" />
                                                        <span className="text-xs text-neutral-600 dark:text-neutral-300">
                                                            {billing.subscriptionName}
                                                            {billing.deductedCount && ` (${billing.deductedCount}회)`}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">직접입력</span>
                                                )}
                                            </td>
                                            {/* 이용권 사용 금액 */}
                                            <td className="px-4 py-3 text-right">
                                                {billing.voucherSettlementAmount ? (
                                                    <span className="text-sm text-blue-600">
                                                        -{formatAmount(billing.voucherSettlementAmount)}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">-</span>
                                                )}
                                            </td>
                                            {/* 정산금 (totalPaidAmount) with hover tooltip */}
                                            <td className="px-4 py-3 text-right">
                                                <TooltipProvider delayDuration={100}>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <span className="text-sm font-semibold text-green-600 cursor-help underline decoration-dotted decoration-green-400">
                                                                {formatAmount(billing.totalPaidAmount || 0)}
                                                            </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="top" align="end" className="p-0 bg-transparent border-0 shadow-none">
                                                            <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl border border-neutral-200 dark:border-neutral-700 p-3 min-w-[220px] text-left">
                                                                <div className="flex items-center justify-start gap-2 mb-2 border-b pb-1">
                                                                    <Coins className="h-3 w-3" />
                                                                    <p className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">정산 계산식</p>
                                                                </div>

                                                                <div className="space-y-1 text-xs">
                                                                    {/* 기본요금 */}
                                                                    <div className="flex justify-between">
                                                                        <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">기본요금</span>
                                                                        <span className="font-medium">{formatAmount(billing.baseAmount || 0)}</span>
                                                                    </div>
                                                                    {/* 활동비 */}
                                                                    {(billing.additionalBillingItemTotal || 0) > 0 && (
                                                                        <div className="flex justify-between">
                                                                            <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">+ 활동비</span>
                                                                            <span className="font-medium">{formatAmount(billing.additionalBillingItemTotal || 0)}</span>
                                                                        </div>
                                                                    )}
                                                                    {/* 할인 */}
                                                                    {(billing.discountVoucherTotal || 0) > 0 && (
                                                                        <div className="flex justify-between text-green-600">
                                                                            <span>- 할인</span>
                                                                            <span>{formatAmount(billing.discountVoucherTotal || 0)}</span>
                                                                        </div>
                                                                    )}
                                                                    {/* 이용권 정산금 */}
                                                                    {(billing.voucherSettlementAmount || 0) > 0 && (
                                                                        <div className="flex justify-between text-blue-600">
                                                                            <span>- 이용권 정산</span>
                                                                            <span>{formatAmount(billing.voucherSettlementAmount || 0)}</span>
                                                                        </div>
                                                                    )}
                                                                    {/* 최종금액 */}
                                                                    <div className="flex justify-between pt-1 border-t border-dashed">
                                                                        <span className="text-neutral-700 dark:text-neutral-200 font-medium">= 최종금액</span>
                                                                        <span className="font-semibold">{formatAmount(billing.finalAmount || 0)}</span>
                                                                    </div>
                                                                    {/* 결제/환불 구분선 */}
                                                                    <div className="my-1 border-t" />
                                                                    {/* 결제금액 */}
                                                                    <div className="flex justify-between text-green-600">
                                                                        <span>결제금액</span>
                                                                        <span>+{formatAmount(billing.totalPaidAmount || 0)}</span>
                                                                    </div>
                                                                    {/* 환불금액 */}
                                                                    {(billing.totalRefundedAmount || 0) > 0 && (
                                                                        <div className="flex justify-between text-red-600">
                                                                            <span>환불금액</span>
                                                                            <span>-{formatAmount(billing.totalRefundedAmount || 0)}</span>
                                                                        </div>
                                                                    )}
                                                                    {/* 정산금 */}
                                                                    <div className="flex justify-between pt-1 border-t border-dashed font-semibold text-green-700">
                                                                        <span>= 정산금</span>
                                                                        <span>{formatAmount((billing.totalPaidAmount || 0) - (billing.totalRefundedAmount || 0))}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </td>
                                            {/* 최종금액 */}
                                            <td className="px-4 py-3 text-right">
                                                <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                                                    {formatAmount(billing.finalAmount)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {billing.paymentMethod ? (
                                                    <span className="text-sm text-neutral-700 dark:text-neutral-200">
                                                        {getPaymentMethodLabel(billing.paymentMethod)}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">-</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className={cn(
                                                    "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
                                                    statusStyle.bg,
                                                    statusStyle.text
                                                )}>
                                                    {statusStyle.emoji} {getBillingStatusLabel(billing.status)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <div className="relative">
                                                    <button
                                                        onClick={() => setOpenActionMenuId(
                                                            openActionMenuId === billing.id ? null : billing.id
                                                        )}
                                                        className="p-1 rounded hover:bg-neutral-100 dark:bg-neutral-800 dark:hover:bg-neutral-800"
                                                    >
                                                        <MoreVertical className="h-4 w-4 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500" />
                                                    </button>
                                                    {openActionMenuId === billing.id && (
                                                        <div className="absolute right-0 mt-1 w-32 bg-white dark:bg-neutral-900 rounded-lg shadow-lg border z-50">
                                                            <button
                                                                onClick={() => handleViewDetail(billing)}
                                                                className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 dark:bg-neutral-800 dark:hover:bg-neutral-800 flex items-center gap-2"
                                                            >
                                                                <Eye className="h-4 w-4" />
                                                                상세보기
                                                            </button>
                                                            <button
                                                                onClick={() => handleEdit(billing)}
                                                                className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 dark:bg-neutral-800 dark:hover:bg-neutral-800 flex items-center gap-2"
                                                            >
                                                                <Edit2 className="h-4 w-4" />
                                                                수정
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(billing)}
                                                                className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800 dark:bg-neutral-800 flex items-center gap-2 text-red-600"
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                                삭제
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* 페이지네이션 */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                            총 {sortedBillings.length}건 중 {(page - 1) * itemsPerPage + 1}-{Math.min(page * itemsPerPage, sortedBillings.length)}건
                        </p>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                            >
                                이전
                            </Button>
                            <span className="text-sm text-neutral-600 dark:text-neutral-300">
                                {page} / {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                            >
                                다음
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* 정산 상세 모달 */}
            {showDetailModal && selectedBilling && (
                <BillingDetailModal
                    billing={selectedBilling}
                    onClose={() => {
                        setShowDetailModal(false);
                        setSelectedBilling(null);
                    }}
                />
            )}
        </div>
    );
}
